// Chrome for /claim — a sibling of components/Shell (patient funnel) and
// providers/ProviderShell (application). Same visual system: logo header, centred
// column, optional footnote. No progress bar: the claim is a single step, and a
// fake progress bar would be a lie.
import type { ReactNode } from 'react';
import { useIsNarrow } from '../lib/useIsNarrow';

export function ClaimShell({
  badge,
  footnote,
  children,
}: {
  badge?: string;
  footnote?: ReactNode;
  children: ReactNode;
}) {
  const narrow = useIsNarrow();
  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          maxWidth: 1152,
          margin: '0 auto',
          width: '100%',
          padding: narrow ? '14px 20px' : '18px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <img src="/expy-man.png" alt="" width={26} height={26} style={{ display: 'block' }} />
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>
          Expy Health
        </span>
        {badge && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: 'var(--teal-deep)',
              background: 'var(--teal-tint)',
              borderRadius: 6,
              padding: '3px 8px',
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <div style={{ height: 4, background: 'var(--teal-tint)' }} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          className="fade-in"
          style={{
            maxWidth: 520,
            width: '100%',
            margin: '0 auto',
            padding: narrow ? '32px 20px 60px' : '56px 24px 32px',
          }}
        >
          {children}
        </div>
      </div>

      {footnote && (
        <div style={{ maxWidth: 520, width: '100%', margin: '0 auto', padding: '0 24px 36px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden>
              <path
                d="M3.5 8.4l2.8 2.8L12.5 5"
                stroke="var(--teal-deep)"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--teal-deep)' }}>{footnote}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// The round tinted icon badge used on every terminal (non-form) claim screen.
export function IconBadge({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 18,
        background: 'var(--teal-tint)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 22,
      }}
    >
      {children}
    </div>
  );
}
